import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Tree,
  Switch,
  Row,
  Col,
  Statistic,
  Tooltip,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  SettingOutlined,
  UserOutlined,
  SafetyOutlined,
} from '@ant-design/icons';

const RoleManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

  // 模拟角色数据
  const roleData = [
    {
      id: 1,
      name: '超级管理员',
      code: 'super_admin',
      description: '拥有系统所有权限，可以管理所有功能模块',
      userCount: 1,
      status: 'active',
      createTime: '2025-01-01 10:00:00',
      updateTime: '2025-10-24 14:30:00',
      permissions: ['dashboard', 'memory', 'profile', 'script', 'agent', 'knowledge', 'workflow', 'debug', 'model', 'reports', 'audit', 'settings', 'account', 'role', 'menu'],
    },
    {
      id: 2,
      name: '系统管理员',
      code: 'admin',
      description: '负责系统配置和用户管理，拥有大部分管理权限',
      userCount: 3,
      status: 'active',
      createTime: '2025-01-15 09:30:00',
      updateTime: '2025-10-24 13:45:00',
      permissions: ['dashboard', 'memory', 'profile', 'script', 'agent', 'knowledge', 'workflow', 'debug', 'model', 'reports', 'account', 'role'],
    },
    {
      id: 3,
      name: '业务管理员',
      code: 'manager',
      description: '负责业务功能管理，可以配置话术、智能体等',
      userCount: 8,
      status: 'active',
      createTime: '2025-02-01 14:20:00',
      updateTime: '2025-10-24 12:20:00',
      permissions: ['dashboard', 'memory', 'profile', 'script', 'agent', 'knowledge', 'workflow', 'debug', 'reports'],
    },
    {
      id: 4,
      name: '操作员',
      code: 'operator',
      description: '负责日常操作，可以查看和使用大部分功能',
      userCount: 15,
      status: 'active',
      createTime: '2025-03-01 11:15:00',
      updateTime: '2025-10-24 10:30:00',
      permissions: ['dashboard', 'memory', 'profile', 'script', 'agent', 'knowledge', 'workflow', 'debug', 'reports'],
    },
    {
      id: 5,
      name: '查看者',
      code: 'viewer',
      description: '只能查看数据，不能进行修改操作',
      userCount: 25,
      status: 'active',
      createTime: '2025-04-01 15:45:00',
      updateTime: '2025-10-20 16:10:00',
      permissions: ['dashboard', 'reports'],
    },
  ];

  // 权限树数据
  const permissionTreeData = [
    {
      title: '仪表盘',
      key: 'dashboard',
      children: [
        { title: '查看仪表盘', key: 'dashboard:view' },
        { title: '导出数据', key: 'dashboard:export' },
      ],
    },
    {
      title: '记忆管理',
      key: 'memory',
      children: [
        { title: '查看记忆', key: 'memory:view' },
        { title: '编辑记忆', key: 'memory:edit' },
        { title: '删除记忆', key: 'memory:delete' },
        { title: '导入记忆', key: 'memory:import' },
        { title: '导出记忆', key: 'memory:export' },
      ],
    },
    {
      title: '用户画像',
      key: 'profile',
      children: [
        { title: '查看画像', key: 'profile:view' },
        { title: '编辑画像', key: 'profile:edit' },
        { title: '删除画像', key: 'profile:delete' },
        { title: '分析画像', key: 'profile:analyze' },
        { title: '导入画像', key: 'profile:import' },
        { title: '导出画像', key: 'profile:export' },
      ],
    },
    {
      title: '话术管理',
      key: 'script',
      children: [
        { title: '查看话术', key: 'script:view' },
        { title: '编辑话术', key: 'script:edit' },
        { title: '删除话术', key: 'script:delete' },
        { title: '测试话术', key: 'script:test' },
        { title: '导入话术', key: 'script:import' },
        { title: '导出话术', key: 'script:export' },
      ],
    },
    {
      title: '智能体管理',
      key: 'agent',
      children: [
        { title: '查看智能体', key: 'agent:view' },
        { title: '编辑智能体', key: 'agent:edit' },
        { title: '删除智能体', key: 'agent:delete' },
        { title: '部署智能体', key: 'agent:deploy' },
        { title: '测试智能体', key: 'agent:test' },
      ],
    },
    {
      title: '知识库管理',
      key: 'knowledge',
      children: [
        { title: '查看知识库', key: 'knowledge:view' },
        { title: '编辑知识库', key: 'knowledge:edit' },
        { title: '删除知识库', key: 'knowledge:delete' },
        { title: '同步知识库', key: 'knowledge:sync' },
        { title: '导入知识库', key: 'knowledge:import' },
        { title: '导出知识库', key: 'knowledge:export' },
      ],
    },
    {
      title: '工作流管理',
      key: 'workflow',
      children: [
        { title: '查看工作流', key: 'workflow:view' },
        { title: '编辑工作流', key: 'workflow:edit' },
        { title: '删除工作流', key: 'workflow:delete' },
        { title: '部署工作流', key: 'workflow:deploy' },
        { title: '测试工作流', key: 'workflow:test' },
      ],
    },
    {
      title: '调试工具',
      key: 'debug',
      children: [
        { title: '查看调试工具', key: 'debug:view' },
        { title: '使用调试工具', key: 'debug:test' },
        { title: 'A/B测试', key: 'debug:ab_test' },
      ],
    },
    {
      title: '大模型管理',
      key: 'model',
      children: [
        { title: '查看模型', key: 'model:view' },
        { title: '编辑模型', key: 'model:edit' },
        { title: '删除模型', key: 'model:delete' },
        { title: '测试模型', key: 'model:test' },
      ],
    },
    {
      title: '报表管理',
      key: 'reports',
      children: [
        { title: '查看报表', key: 'reports:view' },
        { title: '导出报表', key: 'reports:export' },
      ],
    },
    {
      title: '系统管理',
      key: 'system',
      children: [
        { title: '审计日志', key: 'audit:view' },
        { title: '系统设置', key: 'settings:view' },
        { title: '账号管理', key: 'account:view' },
        { title: '角色管理', key: 'role:view' },
        { title: '菜单管理', key: 'menu:view' },
      ],
    },
  ];

  const handleAddRole = () => {
    setSelectedRecord(null);
    form.resetFields();
    setCheckedKeys([]);
    setEditModalVisible(true);
  };

  const handleEditRole = (record: any) => {
    setSelectedRecord(record);
    form.setFieldsValue(record);
    setCheckedKeys(record.permissions || []);
    setEditModalVisible(true);
  };

  const handleViewDetail = (record: any) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const handleManagePermission = (record: any) => {
    setSelectedRecord(record);
    setCheckedKeys(record.permissions || []);
    setPermissionModalVisible(true);
  };

  const handleSaveRole = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (selectedRecord) {
        message.success('角色更新成功！');
      } else {
        message.success('角色创建成功！');
      }
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermission = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('权限配置保存成功！');
      setPermissionModalVisible(false);
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = (record: any) => {
    if (record.userCount > 0) {
      message.error('该角色下还有用户，无法删除');
      return;
    }
    message.loading('正在删除角色...', 1);
    setTimeout(() => {
      message.success(`已删除角色 ${record.name}`);
    }, 1000);
  };

  const handleToggleStatus = (record: any) => {
    const newStatus = record.status === 'active' ? 'inactive' : 'active';
    message.loading('正在更新状态...', 1);
    setTimeout(() => {
      message.success(`角色 ${record.name} 已${newStatus === 'active' ? '启用' : '禁用'}`);
    }, 1000);
  };

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的角色');
      return;
    }
    message.loading('正在批量删除...', 1);
    setTimeout(() => {
      message.success(`已删除 ${selectedRowKeys.length} 个角色`);
      setSelectedRowKeys([]);
    }, 1000);
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'green' : 'red';
  };

  const getStatusName = (status: string) => {
    return status === 'active' ? '启用' : '禁用';
  };

  const columns = [
    {
      title: '角色信息',
      key: 'roleInfo',
      width: 300,
      fixed: 'left' as const,
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <SafetyOutlined style={{ fontSize: 20, color: '#1890ff', marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>{record.name}</div>
            <div style={{ fontSize: 12, color: '#999', marginBottom: 2 }}>{record.code}</div>
            <div style={{ fontSize: 12, color: '#666', lineHeight: 1.4 }}>{record.description}</div>
          </div>
        </div>
      ),
    },
    {
      title: '用户数量',
      key: 'userCount',
      width: 100,
      render: (_: any, record: any) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>
            {record.userCount}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>用户</div>
        </div>
      ),
    },
    {
      title: '权限数量',
      key: 'permissionCount',
      width: 100,
      render: (_: any, record: any) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 'bold', color: '#52c41a' }}>
            {record.permissions?.length || 0}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>权限</div>
        </div>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_: any, record: any) => (
        <Tag color={getStatusColor(record.status)}>
          {getStatusName(record.status)}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 150,
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="查看详情">
            <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
              详情
            </Button>
          </Tooltip>
          <Tooltip title="编辑角色">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEditRole(record)}>
              编辑
            </Button>
          </Tooltip>
          <Tooltip title="权限配置">
            <Button type="link" icon={<SettingOutlined />} onClick={() => handleManagePermission(record)}>
              权限
            </Button>
          </Tooltip>
          <Tooltip title={record.status === 'active' ? '禁用角色' : '启用角色'}>
            <Button 
              type="link" 
              onClick={() => handleToggleStatus(record)}
            >
              {record.status === 'active' ? '禁用' : '启用'}
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这个角色吗？"
            description="删除后无法恢复，请谨慎操作"
            onConfirm={() => handleDeleteRole(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除角色">
              <Button type="link" icon={<DeleteOutlined />} danger disabled={record.userCount > 0}>
                删除
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredData = roleData.filter(item => {
    return !searchText || 
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.code.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description.toLowerCase().includes(searchText.toLowerCase());
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div style={{ padding: 24 }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="总角色数" value={roleData.length} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="启用角色" value={roleData.filter(item => item.status === 'active').length} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="总用户数" value={roleData.reduce((sum, item) => sum + item.userCount, 0)} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="权限总数" value={permissionTreeData.length} />
          </Card>
        </Col>
      </Row>

      <Card
        title="角色管理"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => message.info('数据已刷新')}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddRole}
            >
              新增角色
            </Button>
          </Space>
        }
      >
        {/* 搜索 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="搜索角色名称、编码或描述"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
            />
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定要删除选中的 ${selectedRowKeys.length} 个角色吗？`}
                onConfirm={handleBatchDelete}
                okText="确定"
                cancelText="取消"
              >
                <Button danger icon={<DeleteOutlined />}>
                  批量删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          scroll={{ x: 1400 }}
          size="middle"
        />
      </Card>

      {/* 编辑角色模态框 */}
      <Modal
        title={selectedRecord ? '编辑角色' : '新增角色'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveRole}
        >
          <Form.Item
            name="name"
            label="角色名称"
            rules={[{ required: true, message: '请输入角色名称' }]}
          >
            <Input placeholder="请输入角色名称" />
          </Form.Item>

          <Form.Item
            name="code"
            label="角色编码"
            rules={[{ required: true, message: '请输入角色编码' }]}
          >
            <Input placeholder="请输入角色编码" />
          </Form.Item>

          <Form.Item
            name="description"
            label="角色描述"
            rules={[{ required: true, message: '请输入角色描述' }]}
          >
            <Input.TextArea rows={3} placeholder="请输入角色描述" />
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
            initialValue="active"
          >
            <Switch
              checkedChildren="启用"
              unCheckedChildren="禁用"
              defaultChecked
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                保存
              </Button>
              <Button onClick={() => setEditModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 权限配置模态框 */}
      <Modal
        title={`权限配置 - ${selectedRecord?.name}`}
        open={permissionModalVisible}
        onCancel={() => setPermissionModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setPermissionModalVisible(false)}>
            取消
          </Button>,
          <Button key="save" type="primary" loading={loading} onClick={handleSavePermission}>
            保存权限
          </Button>,
        ]}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <p>请选择该角色可以访问的权限：</p>
        </div>
        <Tree
          checkable
          checkedKeys={checkedKeys}
          onCheck={(checked) => {
            if (Array.isArray(checked)) {
              setCheckedKeys(checked);
            } else {
              setCheckedKeys(checked.checked);
            }
          }}
          treeData={permissionTreeData}
          height={400}
        />
      </Modal>

      {/* 角色详情模态框 */}
      <Modal
        title="角色详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={600}
      >
        {selectedRecord && (
          <div>
            <Row gutter={16}>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <SafetyOutlined style={{ fontSize: 60, color: '#1890ff' }} />
                  <div style={{ marginTop: 8, fontWeight: 'bold', fontSize: 18 }}>{selectedRecord.name}</div>
                  <div style={{ color: '#666' }}>{selectedRecord.code}</div>
                </div>
              </Col>
              <Col span={16}>
                <div style={{ marginBottom: 16 }}>
                  <h4>基本信息</h4>
                  <p><strong>角色名称:</strong> {selectedRecord.name}</p>
                  <p><strong>角色编码:</strong> {selectedRecord.code}</p>
                  <p><strong>状态:</strong> <Tag color={getStatusColor(selectedRecord.status)}>{getStatusName(selectedRecord.status)}</Tag></p>
                  <p><strong>用户数量:</strong> {selectedRecord.userCount} 人</p>
                </div>
                
                <div style={{ marginBottom: 16 }}>
                  <h4>描述信息</h4>
                  <p>{selectedRecord.description}</p>
                </div>

                <div>
                  <h4>时间信息</h4>
                  <p><strong>创建时间:</strong> {selectedRecord.createTime}</p>
                  <p><strong>更新时间:</strong> {selectedRecord.updateTime}</p>
                </div>
              </Col>
            </Row>
            
            <Divider />
            
            <div>
              <h4>权限列表</h4>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {selectedRecord.permissions?.map((permission: string, index: number) => (
                  <Tag key={index} style={{ marginBottom: 4 }}>
                    {permission}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RoleManagement;
